import { captureDiagnosisLexiconId, midiToNoteLabel, type CaptureDiagnosis } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import type { CalibrationState } from './pitch-calibration.js'
import { formatCaptureReadout } from './capture-readout.js'
import { PitchInputMeter, type PitchMeterState } from './pitch-input-meter.js'
import { useVocalCopy } from './use-vocal-copy.js'
import styles from './pitch-calibration-panel.module.css'

/**
 * The two-take range calibration (§5.8).
 *
 * The per-step instruction is a UI label — "sing your lowest comfortable note" is an
 * *instruction*, not a verdict about the singer — so it is written here. Everything that judges
 * what was heard comes from the lexicon.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.8, §9
 */

const STEP_PROMPT: Record<CalibrationState['step'], string> = {
  /** "Comfortable" is load-bearing: a singer pushed to their extremes skews the middle-60 % band (GH #2328 item 7) */
  lowest: 'Step 1 of 2 — your lowest comfortable note. Hold it for a second.',
  highest: 'Step 2 of 2 — your highest comfortable note. Hold it for a second.',
  done: 'Both takes are in.',
}

export interface PitchCalibrationPanelProps {
  state: CalibrationState
  /** True while the microphone is open for the current take. */
  listening: boolean
  /** Why the last take heard nothing, when it heard nothing. */
  diagnosis: CaptureDiagnosis | null
  /** Live input. This is where a dead microphone is first met, so the meter shows here too. */
  meter: PitchMeterState
  onSing: () => void
  onCancel: () => void
  onDone: () => void
}

export const PitchCalibrationPanel: FC<PitchCalibrationPanelProps> = props => {
  const { state, listening, diagnosis, meter, onSing, onCancel, onDone } = props
  const intro = useVocalCopy('vocal-calibrate-first')
  const privacy = useVocalCopy('vocal-privacy')
  const nothingHeard = useVocalCopy(captureDiagnosisLexiconId(diagnosis))
  const calibrated = useVocalCopy('vocal-range-calibrated', {
    low: state.low === null ? undefined : midiToNoteLabel(state.low),
    high: state.high === null ? undefined : midiToNoteLabel(state.high),
  })
  const done = state.step === 'done'

  return (
    <section className={styles.panel} aria-label="Range calibration">
      <h2 className={styles.title}>Calibrate your range</h2>
      {intro ? (
        <p className={styles.intro}>
          <TheoryGlyphs>{intro.body}</TheoryGlyphs>
        </p>
      ) : null}

      <p className={styles.prompt}>{STEP_PROMPT[state.step]}</p>

      <div className={styles.status} role="status" aria-label="Capture">
        {listening ? 'Listening…' : 'Ready when you are'}
      </div>

      <PitchInputMeter frame={meter.frame} noiseFloorRms={meter.noiseFloorRms} deviceLabel={meter.deviceLabel} />

      {state.nothingHeard && nothingHeard ? (
        <p className={styles.problem}>
          <TheoryGlyphs>{nothingHeard.body}</TheoryGlyphs>
        </p>
      ) : null}
      {state.nothingHeard && diagnosis ? <p className={styles.readout}>{formatCaptureReadout(diagnosis)}</p> : null}

      {done && calibrated ? (
        <p className={styles.result}>
          <TheoryGlyphs>{calibrated.body}</TheoryGlyphs>
        </p>
      ) : null}

      {/* §9: said where the microphone is asked for (Sing opens it), not only on the welcome screen */}
      {!done && privacy ? (
        <p className={styles.privacy}>
          <TheoryGlyphs>{privacy.body}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.actions}>
        {done ? (
          <Button variant="contained" color="primary" onClick={onDone}>
            Done
          </Button>
        ) : (
          <Button variant="contained" color="primary" disabled={listening} onClick={onSing}>
            Sing
          </Button>
        )}
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  )
}
